import { useState } from 'react'
import { Search } from 'lucide-react'
import type { NtTerm } from '../../core/types'
import { useAllTerms, useAwards, usePlacement, useCourses } from './model'
import { Empty } from '../../ui/States'

/**
 * Past Norse Terms.
 *
 * Nothing new is stored for this — a term's history *is* its placement
 * document plus its awards, which is exactly why placements are kept per term
 * rather than overwritten. Next year is a new term, not a wiped database.
 *
 * The student lookup is the part people actually use: a counsellor writing a
 * recommendation wants "what has this kid done for four Marches", and it is
 * otherwise scattered across four spreadsheets nobody kept.
 */
export function History({ current, canSeeStudents }: {
  current: NtTerm
  canSeeStudents: boolean
}) {
  const terms = useAllTerms()
  const [termId, setTermId] = useState(current.id)
  const past = terms.filter((t) => t.id !== current.id)

  if (terms.length === 0) {
    return <Empty title="No terms yet" body="Once a term has run, it stays here." />
  }

  return (
    <>
      {terms.length > 1 && (
        <div className="chips" style={{ marginTop: 'var(--s-2)' }}>
          {terms.map((t) => (
            <button key={t.id} className={`chip${termId === t.id ? ' is-on' : ''}`}
              onClick={() => setTermId(t.id)}>
              {t.label}{t.id === current.id ? ' · current' : ''}
            </button>
          ))}
        </div>
      )}

      <TermSummary termId={termId} canSeeStudents={canSeeStudents} />

      {past.length === 0 && (
        <p className="muted-note">
          Only this term so far. When you set up next year's, this one stays here with its
          courses, placement and awards intact.
        </p>
      )}
    </>
  )
}

function TermSummary({ termId, canSeeStudents }: { termId: string; canSeeStudents: boolean }) {
  const { courses } = useCourses(termId)
  const placement = usePlacement(termId, canSeeStudents)
  const awards = useAwards(termId)
  const [q, setQ] = useState('')

  const ran = (courses ?? []).filter((c) => c.status === 'approved' && c.block)
  const byId = new Map((courses ?? []).map((c) => [c.id, c]))

  const hits = !q.trim() || !placement ? [] : placement.rows.filter((r) =>
    r.name.toLowerCase().includes(q.trim().toLowerCase()))

  return (
    <>
      <div className="card" style={{ marginTop: 'var(--s-3)' }}>
        <div className="dl">
          <div><div className="dl__k">Courses run</div><div className="dl__v">{ran.length}</div></div>
          <div><div className="dl__k">Students placed</div>
            <div className="dl__v">{placement ? placement.rows.length : '—'}</div></div>
          <div><div className="dl__k">Independent</div>
            <div className="dl__v">{placement ? placement.rows.filter((r) => r.independent).length : '—'}</div></div>
          <div><div className="dl__k">Awards</div><div className="dl__v">{awards.length}</div></div>
        </div>
      </div>

      {canSeeStudents && placement && (
        <>
          <h2 className="work__heading">Look up a student</h2>
          <div style={{ position: 'relative' }}>
            <input className="opt__input" placeholder="Last name" aria-label="Look up a student"
              value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          {q.trim() && (
            hits.length === 0
              ? <p className="opt__hint">Nobody by that name in this term.</p>
              : (
                <ul className="work" style={{ marginTop: 'var(--s-2)' }}>
                  {hits.slice(0, 20).map((r) => (
                    <li key={r.studentId} className="work__row">
                      <div className="work__main">
                        <div style={{ fontWeight: 610 }}>{r.name} <span className="cat__meta">gr {r.grade}</span></div>
                        <div className="work__sub">
                          {r.independent
                            ? `Independent: ${r.independentTitle}`
                            : `AM ${byId.get(r.amCourseId ?? '')?.title ?? '—'} · PM ${byId.get(r.pmCourseId ?? '')?.title ?? '—'}`}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )
          )}
        </>
      )}

      {!canSeeStudents && (
        <p className="opt__hint" style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
          <Search size={14} aria-hidden="true" style={{ marginTop: 2, flex: '0 0 auto' }} />
          Student schedules are only visible to whoever runs placement.
        </p>
      )}

      {awards.length > 0 && (
        <>
          <h2 className="work__heading">Awards</h2>
          <ul className="work">
            {awards.map((a) => (
              <li key={a.id} className="work__row">
                <div className="work__main">
                  <div style={{ fontWeight: 610 }}>{a.category}</div>
                  <div className="work__sub">{a.courseTitle || a.studentName || '—'}</div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {ran.length > 0 && (
        <>
          <h2 className="work__heading">What ran</h2>
          <ul className="work">
            {ran.map((c) => (
              <li key={c.id} className="work__row">
                <div className="work__main">
                  <div style={{ fontWeight: 610 }}>{c.title}</div>
                  <div className="work__sub">{c.block} · {c.teacherName}</div>
                </div>
                {placement && (
                  <span className="pill">{placement.seats[c.id] ?? 0}</span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  )
}
